/**
 * Composer — message input for the V2 drawer. Autosizing textarea, Enter to
 * send / Shift+Enter for newline, "/" opens the existing SlashCommandMenu,
 * ScopeChips summarise the outgoing scope, and the map pick/draw toggle plus
 * DataSourcePicker sit on the toolbar. Send swaps to Cancel while running.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  MousePointer2Icon,
  SendHorizonalIcon,
  SlashIcon,
  SquareIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import SlashCommandMenu from '../SlashCommandMenu';
import type { AICommand } from '../hooks/useAICommands';
import DataSourcePicker from './DataSourcePicker';
import ScopeChips, { buildScopeChips } from './ScopeChips';
import type { RealtimeSelection } from './types';
import '../ChatDisasterGPT.css';

export const COMPOSER_PLACEHOLDER =
  'Message PRATUS AI Chat...  (type / for commands)';

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  running: boolean;
  onCancel: () => void;
  /** Validation hint shown under the input (e.g. SITREP needs a selection). */
  hint?: string;
  commands: AICommand[];
  selections: RealtimeSelection[];
  setSelections: React.Dispatch<React.SetStateAction<RealtimeSelection[]>>;
  incidentName?: string;
  aiLayerPickMode?: boolean;
  onToggleAiLayerPickMode?: () => void;
  /** Increment to focus the textarea (e.g. after a queued skill prompt). */
  focusNonce?: number;
  /** Item a pending form draft will update, shown under the textarea. */
  draftLabel?: string;
}

const Composer: React.FC<ComposerProps> = ({
  value,
  onChange,
  onSubmit,
  canSubmit,
  running,
  onCancel,
  hint,
  commands,
  selections,
  setSelections,
  incidentName,
  aiLayerPickMode = false,
  onToggleAiLayerPickMode,
  focusNonce = 0,
  draftLabel,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const chips = useMemo(
    () => buildScopeChips({ selections, incidentName, setSelections }),
    [selections, incidentName, setSelections]
  );

  const focusEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    textareaRef.current?.focus();
  }, []);

  const selectCommand = useCallback(
    (cmd: AICommand) => {
      onChange(`${cmd.command} `);
      setMenuOpen(false);
      focusEnd();
    },
    [onChange, focusEnd]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === '/' && value.length === 0 && commands.length > 0) {
      e.preventDefault();
      setMenuOpen(true);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  // Refocus the input when a run finishes so the user can keep typing.
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running) textareaRef.current?.focus();
    wasRunning.current = running;
  }, [running]);

  useEffect(() => {
    if (!focusNonce) return;
    const id = window.setTimeout(() => focusEnd(), 50);
    return () => window.clearTimeout(id);
  }, [focusNonce, focusEnd]);

  return (
    <div
      className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:bg-background tw:px-3 tw:pb-3 tw:pt-2"
      data-testid="ai-composer"
      data-tour="pratus-ai-composer"
    >
      <ScopeChips chips={chips} disabled={running} />

      <div className="tw:relative">
        {menuOpen && commands.length > 0 && (
          <SlashCommandMenu
            commands={commands}
            onSelect={selectCommand}
            onClose={closeMenu}
          />
        )}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={COMPOSER_PLACEHOLDER}
          aria-label="Message PRATUS AI"
          disabled={running}
          rows={1}
          className="tw:max-h-48 tw:min-h-10 tw:resize-none tw:py-2.5"
        />
      </div>

      {draftLabel ? (
        <Badge
          variant="secondary"
          data-testid="ai-draft-target"
          className="tw:h-auto tw:max-w-full tw:justify-start tw:whitespace-normal tw:border-violet-400/70! tw:bg-violet-500/15! tw:text-left tw:text-violet-200!"
        >
          Output: {draftLabel}
        </Badge>
      ) : null}

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-1.5">
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="outline"
                  size="icon-sm"
                  id="dgpt-command-trigger"
                  aria-label="Browse commands"
                  aria-expanded={menuOpen}
                  disabled={running || commands.length === 0}
                  onClick={() => setMenuOpen((o) => !o)}
                />
              }
            >
              <SlashIcon />
            </TooltipTrigger>
            <TooltipContent className="dark">Browse commands</TooltipContent>
          </Tooltip>
          {onToggleAiLayerPickMode && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant={aiLayerPickMode ? 'default' : 'outline'}
                    size="icon-sm"
                    aria-label="Pick a map layer, form card, or form box for AI"
                    aria-pressed={aiLayerPickMode}
                    disabled={running}
                    onClick={onToggleAiLayerPickMode}
                  />
                }
              >
                <MousePointer2Icon />
              </TooltipTrigger>
              <TooltipContent className="dark">
                {aiLayerPickMode
                  ? 'Picking… click a map layer, form card, or box (Esc to stop)'
                  : 'Pick a map layer, form card, or form box'}
              </TooltipContent>
            </Tooltip>
          )}
          <DataSourcePicker
            selections={selections}
            setSelections={setSelections}
            disabled={running}
          />
        </div>

        {running ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            aria-label="Cancel current run"
          >
            <SquareIcon /> Cancel
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={onSubmit}
            disabled={!canSubmit}
            aria-label="Send"
            data-testid="ai-send"
          >
            Send <SendHorizonalIcon />
          </Button>
        )}
      </div>

      {hint && (
        <p className={cn('tw:m-0 tw:text-xs tw:text-warning')} role="status">
          {hint}
        </p>
      )}
    </div>
  );
};

export default Composer;
